import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccessManagementOrgUnitCollection,
  AccessManagementOrgUnitCollectionOURecord,
  AccessManagementOrgUnitCollectionRecord,
} from '@cdw-ae/common-data';
import {
  ApiResponse,
  ICdwSearchGridDataService,
} from '@cdw-ae/common-ui-components-search-grid';
import {
  IGetSqlDataParams,
  SelectedFilterField,
  SqlResponse,
} from '@cdw-ae/filter-bar-interfaces';
import { OU_COLLECTIONS_ROUTES } from '@cdw-ae/routes-access-api';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';
import { httpOptions } from '../designations/designations-data.service';

@Injectable({
  providedIn: 'root',
})
export class OrgUnitCollectionsDataService
  implements ICdwSearchGridDataService<AccessManagementOrgUnitCollectionRecord>
{
  constructor(
    private httpClient: HttpClient,
    _accessManagementAuthService: AccessManagementFrontendAuthService,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public fetchSqlData(
    params: IGetSqlDataParams
  ): Observable<SqlResponse<AccessManagementOrgUnitCollectionRecord>> {
    const { activeFilters, startRow, endRow }: IGetSqlDataParams = params;
    return this.getOrgUnitCollections(
      activeFilters,
      startRow,
      endRow
    );
  }

  public getOrgUnitCollections(
    activeFilters?: SelectedFilterField[],
    startRow?: number,
    endRow?: number
  ): Observable<SqlResponse<AccessManagementOrgUnitCollectionRecord>> {
    const url = this.urlBuilder.backendApi('ou-collections', OU_COLLECTIONS_ROUTES.ROOT);
    const queryString = getQueryStringFromParams({
      activeFilters,
      startRow,
      endRow,
    });
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.httpClient.get<SqlResponse<AccessManagementOrgUnitCollectionRecord>>(
      fullUrl
    ).pipe(
      catchError(() => of({ data: [], itemTotal: 0 } as SqlResponse<AccessManagementOrgUnitCollectionRecord>))
    );
  }

  public getOrgUnitCollectionById(
    orgUnitCollectionId: number
  ): Observable<{
    success: boolean;
    data: AccessManagementOrgUnitCollectionRecord & {
      ouCollectionOus: AccessManagementOrgUnitCollectionOURecord[];
    };
  }> {
    const url = this.urlBuilder.backendApi('ou-collections', OU_COLLECTIONS_ROUTES.GET_BY_ID, { id: orgUnitCollectionId });
    return this.httpClient
      .get<
        AccessManagementOrgUnitCollectionRecord & {
          ouCollectionOus: AccessManagementOrgUnitCollectionOURecord[];
        }
      >(
        url,
        httpOptions
      )
      .pipe(
        map((res) => ({
          success: true,
          data: res,
        })),
        catchError(() => of({ success: false, data: null as never }))
      );
  }

  public updateOrgUnitCollection(
    newOrgUnitCollection: Partial<AccessManagementOrgUnitCollection>,
    orgUnitCollectionOUIds: string[],
    orgUnitCollectionId: number
  ): Promise<Observable<ApiResponse<AccessManagementOrgUnitCollectionRecord>>> {
    const url = this.urlBuilder.backendApi('ou-collections', OU_COLLECTIONS_ROUTES.GET_BY_ID, { id: orgUnitCollectionId });
    return Promise.resolve(
      this.httpClient
        .put<AccessManagementOrgUnitCollectionRecord>(
          url,
          { newOrgUnitCollection, orgUnitCollectionOUIds, orgUnitCollectionId },
          httpOptions
        )
        .pipe(
          map(
            (res): ApiResponse<AccessManagementOrgUnitCollectionRecord> => ({
              success: true,
              data: [res],
            })
          ),
          catchError(() => of({ success: false, data: [] } as ApiResponse<AccessManagementOrgUnitCollectionRecord>))
        )
    );
  }

  public canDeleteOrgUnitCollection(orgUnitCollectionId: number): Observable<{ canDelete: boolean; message?: string }> {
    const url = this.urlBuilder.backendApi('ou-collections', OU_COLLECTIONS_ROUTES.CAN_DELETE, { id: orgUnitCollectionId });
    return this.httpClient.get<{ canDelete: boolean; message?: string }>(
      url,
      httpOptions
    ).pipe(
      catchError(() => of({ canDelete: false }))
    );
  }

  public deleteOrgUnitCollection(
    orgUnitCollectionId: number
  ): Promise<Observable<{ success: boolean }>> {
    const url = this.urlBuilder.backendApi('ou-collections', OU_COLLECTIONS_ROUTES.GET_BY_ID, { id: orgUnitCollectionId });
    return Promise.resolve(
      this.httpClient.delete<{ success: boolean }>(
        url,
        {
          ...httpOptions,
          body: { orgUnitCollectionId },
        }
      ).pipe(
        catchError(() => of({ success: false }))
      )
    );
  }

  public addOrgUnitCollection(
    newOrgUnitCollection: Partial<AccessManagementOrgUnitCollection>,
    orgUnitCollectionOUIds: string[]
  ): Observable<ApiResponse<AccessManagementOrgUnitCollectionRecord>> {
    const url = this.urlBuilder.backendApi('ou-collections', OU_COLLECTIONS_ROUTES.ROOT);
    return this.httpClient
      .post(
        url,
        { newOrgUnitCollection, orgUnitCollectionOUIds },
        httpOptions
      )
      .pipe(
        map((res) => ({
          success: true,
          data: [res as AccessManagementOrgUnitCollectionRecord],
        })),
        catchError(() => of({ success: false, data: [] as AccessManagementOrgUnitCollectionRecord[] }))
      );
  }
}
